import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSignedUrls } from '@/hooks/useSignedUrl';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FeaturedInstruments = () => {
  const { t, language } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const { data: instruments = [] } = useQuery({
    queryKey: ['featured-instruments'],
    queryFn: async () => {
      const { data } = await supabase
        .from('instruments')
        .select('*')
        .eq('featured', true)
        .eq('status', 'published')
        .order('display_order')
        .limit(3);
      return data || [];
    },
  });

  const instrumentIds = instruments.map(i => i.id);
  const { data: heroImages = [] } = useQuery({
    queryKey: ['featured-hero-images', instrumentIds],
    queryFn: async () => {
      if (!instrumentIds.length) return [];
      const { data } = await supabase
        .from('gallery_images')
        .select('*')
        .in('instrument_id', instrumentIds)
        .eq('is_visible', true)
        .order('display_order');
      return data || [];
    },
    enabled: instrumentIds.length > 0,
  });

  const imageMap = useMemo(() => {
    const map: Record<string, typeof heroImages[0]> = {};
    for (const img of heroImages) {
      if (!img.instrument_id) continue;
      if (!map[img.instrument_id] || img.role === 'hero') {
        map[img.instrument_id] = img;
      }
    }
    return map;
  }, [heroImages]);

  const paths = useMemo(() => Object.values(imageMap).map(i => i.storage_path), [imageMap]);
  const urls = useSignedUrls(paths);

  if (instruments.length === 0) return null;

  const getTranslated = (inst: typeof instruments[0], field: 'title' | 'description') => {
    const translations = field === 'title' ? inst.title_translations : inst.description_translations;
    if (translations && typeof translations === 'object' && language in (translations as Record<string, string>)) {
      return (translations as Record<string, string>)[language];
    }
    return inst[field] || '';
  };

  return (
    <section className="py-16 md:py-24 lg:py-32 px-6 bg-secondary/30" aria-label={t.featuredSectionTitle}>
      <div ref={ref} className="max-w-6xl mx-auto">
        <p className={`text-xs tracking-[0.3em] uppercase text-muted-foreground mb-8 reveal-on-scroll ${isVisible ? 'revealed' : ''}`}>
          {t.featuredSectionTitle}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 sm:gap-6 md:gap-8">
          {instruments.map((inst, i) => {
            const img = imageMap[inst.id];
            const imgUrl = img ? urls.get(img.storage_path) : '';
            const slug = inst.slug || inst.id;

            return (
              <Link
                key={inst.id}
                to={`/instruments/${slug}`}
                className={`group block reveal-on-scroll ${i > 0 ? 'reveal-on-scroll-delay-1' : ''} ${isVisible ? 'revealed' : ''}`}
              >
                <div className="aspect-[3/4] overflow-hidden bg-secondary/50 mb-5 img-frame">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={img?.alt_text || getTranslated(inst, 'title')}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-serif text-3xl text-muted-foreground/20">{getTranslated(inst, 'title').charAt(0)}</span>
                    </div>
                  )}
                </div>
                <h3 className="font-serif text-xl text-foreground group-hover:text-gold transition-colors duration-300">
                  {getTranslated(inst, 'title')}
                </h3>
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground mt-1">
                  {inst.instrument_type}{inst.year ? ` · ${inst.year}` : ''}
                </p>
                <div className="w-0 h-px bg-gold mt-4 group-hover:w-12 transition-all duration-500" aria-hidden="true" />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedInstruments;
